import React from 'react';
import ObfuscatedEmail from '../components/ObfuscatedEmail';
interface Partner {
    id: string;
    name: string;
    logo: string;
    description: string;
    url: string;
    productImage?: string;
    isFeatured?: boolean;
}

const PartnersSponsorsPage: React.FC = () => {
    const partners: Partner[] = [
        {
            id: 'sustainative',
            name: 'Sustainative',
            logo: '/assets/green-solutions/Sustainative Concepts-logo-final.png',
            description: 'Sustainative is a First Nations led ESG and sustainability advisory that delivers culturally informed solutions, guided by its Indigenous Regenerative Sustainability framework, which weaves ancient wisdom with science and technology to enable respectful decision making, advance reconciliation, and deliver long term climate and health outcomes that nurture the spirit of Country and Community.',
            url: 'https://sustainative.com.au/',
            productImage: '/assets/green-solutions/Traingle solutions of 1st scientists (4).png'
        },
        {
            id: 'ponyup',
            name: 'PonyUp For Good',
            logo: '/assets/green-solutions/IMG_4630.jpeg',
            description: 'PonyUp for Good is a female-led social enterprise providing IT Asset Disposal and sustainable e-waste solutions through reuse and responsible recycling. Their work reduces landfill, extends the life of technology, and generates measurable impact. Fifty percent of profits support SecondBite\'s food rescue programs across Australia.',
            url: 'https://www.ponyupforgood.com/',
            productImage: '/assets/green-solutions/Old tech new life horse (5).png'
        },
        {
            id: 'ecobin',
            name: 'Ecobin',
            logo: '', 
            description: "Ecobin's mission is to make waste separation easier and to help reduce the amount of waste that ends up in landfill. Their bins are made in Melbourne with recycled materials and are designed to be clear and easy to use so you can save your brain power for bigger things.",
            url: 'https://www.ecobin.com.au/',
            productImage: '/assets/green-solutions/Banner Image 3.png'
        },
        {
            id: 'greencollect',
            name: 'Green Collect',
            logo: '/assets/green-solutions/GC_Logo_Horizontal_CMYK.jpg',
            description: 'At Green Collect, discover hand-selected, quality-tested secondhand essentials for your home and business. Whether you are sourcing office furniture on a budget or looking for avenues for your items to be collected, Green Collect has sustainable solutions to help us make the most of everything.',
            url: 'https://greencollect.org/',
            productImage: '/assets/green-solutions/Green_Collect_Refined.png'
        },
        {
            id: 'rehabitat',
            name: 'ReHabitat',
            logo: '/assets/green-solutions/ReHabitat_black.png',
            description: 'ReHabitat develops biodegradable, science-led habitat and planting systems that replace plastic materials used in restoration and revegetation. Their products provide immediate ground cover for wildlife, while planting systems replace plastic tree guards by combining protection with habitat, eliminating retrieval and reducing pollution. Grounded in behavioural ecology and restoration science, ReHabitat supports biodiversity, sustainable monitoring, and post-disturbance recovery.',
            url: 'https://rehabitat.au/',
            productImage: '/assets/green-solutions/ReHabitat.jpg'
        },
    ];

    const renderCardHeader = (partner: Partner) => {
        if (partner.id === 'ponyup') {
            return (
                <div className="relative h-full w-full overflow-hidden">
                    <img src={partner.productImage} alt={partner.name} className="w-full h-full rounded-[22px]" />
                </div>
            );
        }

        if (partner.id === 'sustainative' || partner.id === 'rehabitat') {
            return (
                <div className="grid grid-cols-2 h-full">
                    <div className="flex items-center justify-center p-6 bg-white overflow-hidden">
                        <img 
                            src={partner.logo} 
                            alt={partner.name} 
                            className={`max-h-full max-w-full object-contain ${partner.id === 'sustainative' ? 'scale-120 origin-top -translate-y-2 translate-x-4' : ''}`} 
                        />
                    </div>
                    <div className="flex items-center justify-center p-0 ">
                        <img 
                            src={partner.productImage} 
                            alt={`${partner.name} secondary`} 
                            className={`w-full h-full scale-110 origin-top -translate-x-4 ${partner.id === 'sustainative' ? 'object-contain p-4' : 'object-cover'}`} 
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <img 
                src={partner.productImage || partner.logo} 
                alt={partner.name} 
                className="w-full h-full rounded-[22px]"
            />
        );
    };

    return (
        <section className="bg-[#fdfdf5] min-h-screen">
            <div className="max-w-6xl mx-auto px-6 pb-24">
                <div className="my-12">
                    <h1 className="!text-[42px] font-bold text-[#498c61] !mb-[28px]">Greener Lab Solutions</h1>
                    <h2 className="text-[24px] font-bold text-[#4d4a4a] mb-[34px]">Community & Impact-Led Groups</h2>
                </div>

                {/* Partners Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px] mb-20">
                    {partners.map((partner) => (
                        <div key={partner.id} className="bg-white rounded-[18px] overflow-hidden shadow-[0_10px_40px_-15px_rgba(0,0,0,0.3)] border border-gray-100 flex flex-col h-full transform transition-transform hover:scale-[1.02]">
                            {/* Card Top: Images Section */}
                            <div className="relative h-[250px] overflow-hidden border-b border-gray-50 rounded-[22px]">
                                {renderCardHeader(partner)}
                            </div>

                            {/* Card Bottom: Content Section */}
                            <div className="px-7 py-5 flex flex-col flex-grow !min-h-[200px]">
                                <h3 className="text-[20px] font-bold text-[#4d4a4a] mb-2">{partner.name}</h3>
                                <div className="text-[#3b4b32] overflow-hidden text-[14px]">
                                    {partner.description}
                                </div>
                                <div className="mt-auto flex justify-end">
                                    <a
                                        href={partner.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#498c61] font-bold hover:underline text-[15.5px]"
                                    >
                                        Visit Website
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA Section */}
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-[#508873] mb-12">Help grow our sustainable purchasing options</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* For Suppliers */}
                        <div className="bg-[#d6f3de] rounded-[40px] p-8 flex flex-col items-start text-center">
                            <div className="mb-6 flex items-start justify-start">
                                <svg className="w-20 h-20 text-[#4a9b7e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-[#1a4d3a] mb-3">For Suppliers & Industry</h3>
                            <p className="text-gray-600 mb-8 text-base text-left">Join our network and showcase your eco-friendly products to labs across Australia.</p>
                            <ObfuscatedEmail
                                email="hello@labzero.eco"
                                displayText="Contact Us"
                                subject="Supplier Inquiry - Green Solutions"
                                className="w-full bg-transparent justify-center border-2 border-[#498c61] text-[#498c61] px-10 py-3 rounded-full font-bold transition-colors shadow-sm flex items-center gap-2"
                                wrapperClassName="w-full block"
                                showCopyIcon={false}
                            />
                        </div>

                        {/* For Lab Members */}
                        <div className="bg-[#d6f3de] rounded-[40px] p-8 flex flex-col items-start text-center">
                            <div className="mb-6 flex items-start justify-start">
                                <svg className="w-18 h-18 text-[#4a9b7e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-[#1a4d3a] mb-3">For Lab Members</h3>
                            <p className="text-gray-600 mb-8 text-base text-left">Discover and suggest new sustainable solutions to build a greener future for your lab.</p>
                            <ObfuscatedEmail
                                email="hello@labzero.eco"
                                displayText="Suggest a Product"
                                subject="Product Suggestion - Green Solutions"
                                className="w-full bg-[#498c61] text-white px-10 py-3 justify-center rounded-full font-bold transition-colors shadow-lg flex items-center gap-2"
                                wrapperClassName="w-full block"
                                showCopyIcon={false}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PartnersSponsorsPage;